import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { BusIcon, PlusIcon } from '@repo/ui/icons'
import React, { useState } from 'react'

import { API } from '../../api/apis.config'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import { Paths } from '../../lib/constants/path'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { RadFolderEnumMapping } from '../../lib/types/enums/rad/rad-folder.enum'
import { Rad, RadFilters } from '../../lib/types/models/rad/rad'
import {
  transformRadFiltersToUrl,
  transformUrlToRadFilters
} from '../../lib/utils/filters-mapping/rads'
import RadFilter from './rad-filter'

export const radInitialFilters: RadFilters = { year: new Date().getFullYear() }

const RadListPage = () => {
  const { navigate, navigateWithEvent } = useNavigator()
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<RadFilters>({
      initialFilters: radInitialFilters,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformRadFiltersToUrl,
      transformUrlToFilter: transformUrlToRadFilters
    })

  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])
  const tableRef = React.useRef<DynamicTableRef<Rad>>(null)

  const columns: Column<Rad>[] = [
    {
      accessorKey: 'title',
      header: 'Titre',
      enableSorting: true
    },
    {
      accessorKey: 'typeOfFolder',
      header: 'Types de plis',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge text={RadFolderEnumMapping[row.original.typeOfFolder]} variant="white" />
      )
    },
    {
      accessorKey: 'date',
      header: 'Date et heure',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          text={`${DateFormatter.DDMMYYYY(row.original.date)}, ${DateFormatter.getFormattedTime(row.original.hours)}`}
          variant="grey"
        />
      )
    },
    {
      accessorKey: 'addressName',
      header: 'Nom de lieu',
      enableSorting: true
    },
    {
      accessorKey: 'description',
      header: 'Détails',
      enableSorting: true,
      cell: ({ row }) => valueRender(row.original.description)
    }
  ]

  return (
    <div>
      <UserNavBar title="Réunion avant départ (RADs)" icon={BusIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<Rad, RadFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title="Toutes les réunions avant le départ"
        deleteMessage="Etes-vous sûr de vouloir supprimer la réunion avant le départ?"
        columns={columns}
        sorting={{ id: 'date', desc: false }}
        selectable
        allowSearchParam
        endpoint={API.rads.getAll.url}
        onFiltersClick={() => toggleModal('filters')}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onRowClick={(rad, event) =>
          navigateWithEvent(Paths.RAD_UPDATE_PAGE.replace(':id', rad.id.toString()), event)
        }
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'primary',
            label: 'Ajouter une réunion avant départ',
            fn: () => navigate(Paths.RAD_CREATE_PAGE)
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <RadFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default RadListPage
